/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {Permission} from '../../shared/utils/permission.enum';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {UserService} from '../../shared/services/user.service';
import {Router} from '@angular/router';
import {StickyHeaderIconButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-icon-button.component';
import {Subscription} from 'rxjs';
import {ClaimReportQueryModel} from '../../api/ms-claims/contracts/claim-report-query.model';
import {ClaimsApi} from '../../api/ms-claims/claims.api';
import { HttpErrorResponse } from '@angular/common/http';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {ClaimPositionReportDetailModel} from '../../api/ms-claims/contracts/claim-position-report-detail.model';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {ClaimQueryModel} from '../../api/ms-claims/contracts/claim-query.model';

type WorksheetData = any[][];

@Component({
    selector: 'app-claim-main-page',
    templateUrl: './claim-main-page.component.html',
    styleUrls: ['./claim-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClaimMainPageComponent implements OnInit, OnDestroy {

    public subscriptions = new Subscription();
    public contextActions: StickyHeaderIconButtonComponent[] = [];
    public reportCriteria: ClaimReportQueryModel;
    private readonly EXCEL_DATE_FORMAT: string = 'DD-MM-YYYY';
    private readonly EXCEL_TIME_FORMAT: string = 'HH:mm:ss';

    private readonly fileName: string = 'orcan-claims-report.xlsx';
    private readonly worksheetHeader: (string | number)[] = [
        'Partner No.',
        'Partner Description',
        'Claim Number',
        'Reference Number',
        'Claim Status',
        'Sent date on Date',
        'Sent date on Time',
        'Changed on Date',
        'Changed on Time',
        'Supplying Packing Plant Code',
        'Supplying Packing Plant Description',
        'Claim Code',
        'Claim Code Description',
        'Material Number',
        'Material Description',
        'Model Type',
        'Model code',
        'LOT number',
        'MCDR',
        'SPO number',
        'Subject',
        'Description',
        'Email',
        'Handling Unit',
        'Qty',
        'UoM',
        'Decision Code',
        'Decision Description',
        'Error Code',
        'Error Description',
        'TE Minutes',
        'BI Category'
    ];

    private readonly exportAction: StickyHeaderIconButtonComponent = {
        text: 'Export',
        icon: 'iwp-icon-gen_export_excel',
        click: (): void => {
            this.getClaimsReport({
                searchCriteria: this.reportCriteria.searchCriteria,
                sortCriteria: this.reportCriteria.sortCriteria
            });
        },
        id: 'sticky-header-export-excel-button',
        class: 'btn-text-link',
        dataCy: 'sticky-header-export-excel-button',
        tooltip: 'Export to excel'
    };

    constructor(private loading: LoadingService,
                private router: Router,
                private stickyHeaderService: StickyHeaderService,
                private userService: UserService,
                private claimsApi: ClaimsApi,
                private modalService: NgbModal) {
    }

    ngOnInit(): void {
        setTimeout(() => {
            if (this.userService.hasPermissions([Permission.PARTNER_CLAIM_CREATE])) {
                this.stickyHeaderService.registerMainAction({
                    text: 'New',
                    click: (): void => {
                        this.router.navigate(['/', 'claim', 'new']);
                    },
                    id: 'sticky-header-new-claim-button',
                    class: 'btn-primary',
                    dataCy: 'sticky-header-new-claim-button',
                    tooltip: 'Create new Claim'
                });
            }

            if (this.userService.hasPermissions([
                Permission.PARTNER_CLAIM_EXPORT,
                Permission.ADMIN_CLAIM_EXPORT
            ], 'any')) {
                this.contextActions.push(this.exportAction);
            }

            this.stickyHeaderService.registerContextActions(this.contextActions);
        });
    }

    /**
     * Gets the claims report response from the BE
     * @param queryModel the search and sort criteria for the query
     * Calls {@link export}
     * Opens warning modal if exceed-records-number error is returned
     */
    getClaimsReport(queryModel: ClaimReportQueryModel): void {
        this.exportAction.loading = this.exportAction.disabled = true;
        this.stickyHeaderService.registerContextActions(this.contextActions);

        this.subscriptions.add(this.claimsApi.getClaimsReport(queryModel).subscribe((value) => {
                    this.export(value.positions);
                },
                (error: HttpErrorResponse) => {
                    const confirmModalInstance: ConfirmModalComponent = this.modalService
                            .open(ConfirmModalComponent, {centered: true}).componentInstance;
                    confirmModalInstance.initModal({
                        titleText: 'Warning',
                        subtitleText: error.error.message,
                        alertNotificationSeverity: AlertNotificationSeverity.WARNING,
                        secondaryButtonText: 'Close'
                    } as ConfirmModal);
                }).add(() => {
                    this.exportAction.loading = this.exportAction.disabled = false;
                    this.stickyHeaderService.registerContextActions(this.contextActions);
                })
        );
    }

    /**
     * Sets the search and sort criteria for the Claims report
     * @param query an instance of ClaimReportQueryModel
     */
    setReportCriteria(query: ClaimQueryModel): void {
        this.reportCriteria = query == null ? null : {
            searchCriteria: query.searchCriteria,
            sortCriteria: query.paginationCriteria == null ? null : query.paginationCriteria.sortCriteria
        };
    }

    mapWorksheetData(positions: ClaimPositionReportDetailModel[]): WorksheetData {
        let worksheetData: WorksheetData = [];
        if (positions != null) {
            worksheetData = positions.map(position => [
                position.partner ? position.partner.code : null,
                position.partner ? position.partner.name : null,
                position.claimNumber,
                position.referenceNumber,
                position.claimStatus != null ? position.claimStatus.description : null,
                position.sentDate != null ? {
                    v: moment(position.sentDate).toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                position.sentDate != null ? {
                    v: moment(position.sentDate).toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                position.claimLastChangeDatetime != null ? {
                    v: moment(position.claimLastChangeDatetime).toDate(),
                    t: 'd',
                    z: this.EXCEL_DATE_FORMAT
                } : null,
                position.claimLastChangeDatetime != null ? {
                    v: moment(position.claimLastChangeDatetime).toDate(),
                    t: 'd',
                    z: this.EXCEL_TIME_FORMAT
                } : null,
                position.supplyingPackingPlant != null ? position.supplyingPackingPlant.code : null,
                position.supplyingPackingPlant != null ? position.supplyingPackingPlant.name : null,
                position.claimCode != null ? position.claimCode.code : null,
                position.claimCode != null ? position.claimCode.name : null,
                position.material != null ? position.material.number : null,
                position.material != null ? position.material.description : null,
                position.modelType,
                position.modelCode,
                position.lotNumber,
                position.mcdrNumber,
                position.spoNumber,
                position.subject,
                position.description,
                position.email,
                position.handlingUnit,
                position.quantity,
                position.material != null && position.quantity != null ? position.material.salesUnit : null,
                position.decision != null ? position.decision.code : null,
                position.decision != null ? position.decision.name : null,
                position.claimError != null ? position.claimError.code : null,
                position.claimError != null ? position.claimError.name : null,
                position.timeEffort,
                position.businessCategory != null ? `${position.businessCategory.code} - ${position.businessCategory.name}` : null
            ]);
        }
        const worksheet = [this.worksheetHeader].concat(worksheetData);

        return worksheet;
    }

    export(positions: ClaimPositionReportDetailModel[]): void {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(this.mapWorksheetData(positions), {skipHeader: true});

        // Format the date/time columns, so they don't show up as ######
        worksheet['!cols'] = new Array(this.worksheetHeader.length);
        worksheet['!cols'][5] = {wch: 10.5};
        worksheet['!cols'][6] = {wch: 8.5};

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Claim Report');

        // > The default behavior for all parsers is to generate number cells.
        // > Setting cellDates to true will force the generators to store dates.
        // https://docs.sheetjs.com/#user-content-dates
        XLSX.writeFile(workbook, this.fileName, {cellDates: true});
    }

    ngOnDestroy(): void {
        this.stickyHeaderService.clearActions();
        this.subscriptions.unsubscribe();
    }
}
